import {Injectable, NgZone, Optional} from '@angular/core';
import {TranslationService} from '@ngmedax/translation';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import FroalaEditor from 'froala-editor';
import {QuestionnaireStateService} from '../services/questionnaire-state.service';
import {QuestionnaireVariablesService} from '../services/questionnaire-variables.service';
import {TemplateVariablePickerComponent} from '../components/template-variable-picker/template-variable-picker.component';
import {buildTemplateVariables, TemplateVariable} from './template-variables';
import {createVariableToken, serializeVariableTokens, variableInsertionSpacing} from './template-variable-tokens';
import {partiallySelectedCondition} from './template-condition-tokens';
import {TRANSLATION_EDITOR_SCOPE} from '../../constants';
import {KEYS} from '../../translation-keys';

/** Connects the Froala toolbar to a picker while preserving selection and native undo. */
@Injectable()
export class FroalaTemplateVariablesService {
  private modal: NgbModalRef = null;
  private activeEditor: any = null;

  /** Registers a stateless command shared by every editor instance. */
  public constructor(
    private modals: NgbModal,
    private state: QuestionnaireStateService,
    private variables: QuestionnaireVariablesService,
    private zone: NgZone,
    @Optional() private translation: TranslationService
  ) {
    const froala = FroalaEditor as any;
    if (froala.COMMANDS.qaInsertVariable) {
      return;
    }
    froala.DefineIcon('qaInsertVariable', {
      NAME: '<span class="qa-variable-command">{}</span><span>Variable</span>', template: 'text'
    });
    froala.RegisterCommand('qaInsertVariable', {
      title: 'Insert variable', focus: true, undo: false,
      /** Dispatches to the owning Angular component without retaining that component globally. */
      callback: function() { this.events.trigger('qa.insertVariable'); }
    });
    if (froala.LANGUAGE.de) {
      froala.LANGUAGE.de.translation['Insert variable'] = 'Variable einfügen';
    }
  }

  /** Resolves readable names from current questionnaire titles and the administration language. */
  public getLabels(locale: string): {[path: string]: string} {
    const labels: {[path: string]: string} = {};
    buildTemplateVariables(this.state.getQuestionnaire(), this.variables.getVariableScopes(), locale,
      text => this.translation ? this.translation.translate({text, scope: TRANSLATION_EDITOR_SCOPE}) : text
    ).forEach(variable => labels[variable.path] = variable.label);
    return labels;
  }

  /** Adds a permanently visible picker button after the compact toolbar is built. */
  public configure(options: any, locale: () => string): void {
    options.toolbarButtons.qaVariables = {buttons: ['qaInsertVariable'], buttonsVisible: 1, align: 'left'};
    options.toolbarButtonsMD = options.toolbarButtons;
    options.toolbarButtonsSM = options.toolbarButtons;
    options.toolbarButtonsXS = options.toolbarButtons;
    const self = this;
    const originalGet = options.events['html.get'];
    options.events['html.get'] = function(html: string) {
      const serialized = serializeVariableTokens(html);
      return originalGet ? originalGet.call(this, serialized) ?? serialized : serialized;
    };
    options.events['qa.insertVariable'] = function() {
      self.zone.run(() => self.open(this, locale()));
    };
    const originalInit = options.events.initialized;
    const originalDestroy = options.events.destroy;
    let detach: () => void;
    options.events.initialized = function() {
      const editor = this;
      /** Opens the picker for a clicked token, so its variable can be swapped like in the formula editor. */
      const edit = (event: CustomEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        self.zone.run(() => self.edit(editor, event.target as HTMLElement, locale()));
      };
      editor.el.addEventListener('qa-variable-edit', edit);
      detach = () => editor.el.removeEventListener('qa-variable-edit', edit);
      originalInit && originalInit.call(editor);
    };
    options.events.destroy = function() {
      detach?.();
      if (self.activeEditor === this) {
        self.activeEditor = null;
        self.modal?.dismiss();
      }
      originalDestroy && originalDestroy.call(this);
    };
  }

  /** Captures the caret before the search field receives focus and inserts the selected expression. */
  private open(editor: any, locale: string): void {
    if (this.modal || editor.codeView?.isActive()) {
      return;
    }
    editor.selection.save();
    editor.tooltip.hide();
    this.activeEditor = editor;
    const modal = this.modals.open(TemplateVariablePickerComponent, {
      centered: true, size: 'lg', ariaLabelledBy: 'qa-template-variable-title'
    });
    this.modal = modal;
    const picker = modal.componentInstance as TemplateVariablePickerComponent;
    picker.variables = buildTemplateVariables(
      this.state.getQuestionnaire(), this.variables.getVariableScopes(), locale, label => picker._(label)
    );
    modal.result.then(
      (variable: TemplateVariable) => this.finish(editor, variable),
      () => this.finish(editor)
    );
  }

  /** Shows the picker with the token's current variable marked and replaces the token on selection. */
  private edit(editor: any, token: HTMLElement, locale: string): void {
    if (this.modal || editor.codeView?.isActive() || !editor.el.contains(token)) {
      return;
    }
    editor.tooltip.hide();
    this.activeEditor = editor;
    const modal = this.modals.open(TemplateVariablePickerComponent, {
      centered: true, size: 'lg', ariaLabelledBy: 'qa-template-variable-title'
    });
    this.modal = modal;
    const picker = modal.componentInstance as TemplateVariablePickerComponent;
    picker.variables = buildTemplateVariables(
      this.state.getQuestionnaire(), this.variables.getVariableScopes(), locale, label => picker._(label)
    );
    picker.title = KEYS.EDITOR.CHANGE_TEXT_VARIABLE;
    picker.hint = KEYS.EDITOR.CHANGE_TEXT_VARIABLE_HINT;
    const path = (token.textContent || '').replace(/^\{\{\s*|\s*\}\}$/g, '');
    picker.selected = picker.variables.some(item => item.path === path) ? path : path.replace(/^patient\./, 'client.');
    modal.result.then(
      (variable: TemplateVariable) => this.replace(editor, token, variable),
      () => this.replace(editor, token)
    );
  }

  /** Swaps the clicked token as one undo step and leaves the caret behind it. */
  private replace(editor: any, token: HTMLElement, variable?: TemplateVariable): void {
    const canRestore = this.activeEditor === editor && editor.el?.isConnected && token.isConnected;
    this.modal = null;
    this.activeEditor = null;
    if (!canRestore) {
      return;
    }
    editor.events.focus();
    let target = token;
    if (variable && variable.template !== token.textContent) {
      editor.undo.saveStep();
      target = createVariableToken(variable.template, variable.label || variable.path || variable.template);
      token.replaceWith(target);
      editor.undo.saveStep();
      editor.events.trigger('contentChanged');
    }
    const range = document.createRange();
    range.setStartAfter(target);
    range.collapse(true);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  }

  /** Restores the original selection on insert or cancel and records insertion as one undo step. */
  private finish(editor: any, variable?: TemplateVariable): void {
    const canRestore = this.activeEditor === editor && editor.el?.isConnected;
    this.modal = null;
    this.activeEditor = null;
    if (!canRestore) {
      return;
    }
    editor.events.focus();
    editor.selection.restore();
    if (!variable) {
      return;
    }
    const range: Range = editor.selection.ranges()[0];
    if (partiallySelectedCondition(editor.el, range)) { return; }
    editor.undo.saveStep();
    const before = range.cloneRange();
    const after = range.cloneRange();
    before.selectNodeContents(this.getTextBlock(editor.el, range.startContainer));
    before.setEnd(range.startContainer, range.startOffset);
    after.selectNodeContents(this.getTextBlock(editor.el, range.endContainer));
    after.setStart(range.endContainer, range.endOffset);
    const spacing = variableInsertionSpacing(before.toString(), after.toString());
    const token = createVariableToken(variable.template, variable.label || variable.path || variable.template);
    editor.html.insert(spacing.prefix + token.outerHTML + (spacing.suffix ? '&nbsp;' : ''));
    editor.undo.saveStep();
    editor.events.trigger('contentChanged');
  }

  /** Limits spacing decisions to the current paragraph or other text block. */
  private getTextBlock(root: HTMLElement, node: Node): Element {
    const element = node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement;
    const block = element.closest('p, div, li, td, th, h1, h2, h3, h4, h5, h6, blockquote, pre');
    return block && root.contains(block) ? block : root;
  }
}
